import { listsV2 } from '@ecosystem/newron-design-system';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { sortByKey } from '../../../../utils/helper';
import { SortSettings } from '../../../../utils/types';
import { ColumnConfig, GroupInfo, TableType } from '../types';
import { getSortingValueFromGroup, groupByKey, mapToRootTableData, mapToSortingCallback } from '../utils';
import { getFilteredData } from '../../../utils';

interface GroupedDataHookOptions<T extends object> {
  data: T[];
  groupBy: keyof T;
  getGroupName?: (val: T[keyof T]) => string;
  getGroupSortingValue?: (val: T, key: keyof T) => string | number | Date;
  selectedGroup?: string;
  columnConfig?: ColumnConfig[];
  onSelectGroup?: (group: string) => void;
  searchTerm?: string;
}

export function useGroupedTableData<T extends object>({
  data,
  groupBy,
  getGroupName,
  getGroupSortingValue,
  selectedGroup: initialSelectedGroup,
  columnConfig,
  onSelectGroup,
  searchTerm = '',
}: GroupedDataHookOptions<T>): {
  groups: GroupInfo[];
  selectedGroup: string;
  branchData: T[];
  groupedData: Map<string, T[]>;
  onSelectGroup: (group: string) => void;
  onSortingChange?: (pane: TableType) => (sorting: listsV2.SortingChangeProps) => void;
} {
  const [sortSettings, setSortSettings] = useState<{
    root: SortSettings<GroupInfo> | undefined;
    branch: SortSettings<T> | undefined;
  }>({
    root: undefined,
    branch: undefined,
  });
  const [selectedGroup, setSelectedGroup] = useState('');

  const groupedData = useMemo(() => groupByKey(data, groupBy, getGroupName), [data, groupBy, getGroupName]);

  const groups = useMemo(() => {
    const localGroups = mapToRootTableData(groupedData, groupBy, getGroupSortingValue, searchTerm, columnConfig);
    return sortSettings.root
      ? sortByKey<GroupInfo>(localGroups, sortSettings.root, getSortingValueFromGroup)
      : localGroups;
  }, [sortSettings.root, groupBy, groupedData, getGroupSortingValue, searchTerm, columnConfig]);

  const firstGroup = useMemo(() => (groups.length > 0 ? groups[0].group.label : ''), [groups]);

  const handleGroupChange = useCallback(
    (group: string) => {
      setSelectedGroup(group);
      onSelectGroup?.(group);
    },
    [onSelectGroup]
  );

  // Every time the groupBy changes, reset the selectedGroup to initialSelectedGroup or the first group
  useEffect(() => {
    if (groupBy) {
      handleGroupChange(initialSelectedGroup || firstGroup);
    }
  }, [groupBy, initialSelectedGroup, firstGroup, handleGroupChange]);

  const handleSortingChange = useCallback(
    (pane: TableType) =>
      ({ id, sortingOrder }: { id: string; sortingOrder: listsV2.SortingOrder }) => {
        setSortSettings(prev => ({
          ...prev,
          [pane]: sortingOrder === 'none' ? undefined : { id, sortingOrder },
        }));
      },
    []
  );

  const branchData = useMemo(() => {
    const groupRows = groupedData.get(selectedGroup) ?? [];
    if (!searchTerm) {
      return sortSettings.branch
        ? sortByKey<T>(groupRows, sortSettings.branch, mapToSortingCallback(columnConfig, sortSettings.branch.id))
        : groupRows;
    }

    // Only filter the rows in the selected group
    const filteredRows = getFilteredData(groupRows, searchTerm, columnConfig, groupBy);
    return sortSettings.branch
      ? sortByKey<T>(filteredRows, sortSettings.branch, mapToSortingCallback(columnConfig, sortSettings.branch.id))
      : filteredRows;
  }, [groupedData, selectedGroup, sortSettings.branch, columnConfig, searchTerm, groupBy]);

  return {
    groups,
    selectedGroup,
    branchData,
    groupedData,
    onSelectGroup: handleGroupChange,
    onSortingChange: handleSortingChange,
  };
}
